import {Component, computed, effect, inject, Input, signal} from "@angular/core";
import {ReportingService} from "../../services/reporting.service";
import {FormatCurrencyPipe} from "../../pipes/format-currency.pipe";
import {PdfUtilityService} from "../../services/pdf-utility.service";
import {DatePipe} from "@angular/common";
import {GameTypeEnum} from "../../enum/GameTypeEnum";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {IGameInstanceShared} from "../../interfaces/IGameInstanceShared";
import {WinnerPayoutStatusPipe} from "../../pipes/winner-payout-status.pipe";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {RegionTypes} from "../../enum/RegionTypes";
import {ReportFieldDefinitionsService} from "../../services/report-field-definitions.service";
import {MatDialog} from "@angular/material/dialog";
import {ReportGlossaryComponent} from "../../components/dialogs/report-glossary/report-glossary.component";
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {ICharityContributionsBreakdown} from "../../interfaces/ICharityContributionsBreakdown";
import {IPdfSorting} from "../../interfaces/reporting/IPdfSorting";

@Component({
  selector: 'base-page',
  template: 'base-template'
})
export abstract class ReportBaseComponent {

  // ==========================================================================
  // Injected Services (using inject() for cleaner inheritance)
  // ==========================================================================
  protected fieldDefinitionsService = inject(ReportFieldDefinitionsService);
  protected matDialog = inject(MatDialog);

  // ==========================================================================
  // UI State
  // ==========================================================================
  public gameTypes = GameTypeEnum;
  public errorMessage: string = '';
  public uiState: UIStateEnum = UIStateEnum.ShowLoading;
  public uiStateEnumForTemplate = UIStateEnum;
  public activeGame = signal<IGameQueryResult | null>(null);

  /** Computed signal for PST/QST label based on region */
  pstLabel = computed(() => this.activeGame()?.Region === RegionTypes.Quebec ? 'QST' : 'PST');

  // ==========================================================================
  // Help Toggle System
  // ==========================================================================
  /** Signal for help toggle - controls visibility of field descriptions */
  showHelp = signal<boolean>(false);

  /** Pre-computed map of field descriptions - avoids function calls in template */
  readonly fieldDescriptions: Record<string, string>;

  // ==========================================================================
  // Charity Breakdown Grid
  // ==========================================================================
  public columnApi!: GridApi;

  /** Shared column definitions for charity/group breakdown grid */
  public groupBreakdownReportDefs: ColDef[] = [
    {
      headerName: 'Group',
      resizable: true,
      sortable: true,
      field: 'Name',
      flex: 1
    },
    {
      headerName: 'Gross',
      resizable: true,
      sortable: true,
      field: 'CharityContribution',
      flex: 1,
      valueFormatter: params => {
        const converted = this.currencyPipe.transform(params.value);
        return converted ? converted : '';
      }
    },
    {
      headerName: 'Refund Fees',
      resizable: true,
      sortable: true,
      field: 'RefundAmount',
      flex: 1,
      valueFormatter: params => {
        const converted = this.currencyPipe.transform(params.value);
        return converted ? converted : '';
      }
    },
    {
      headerName: 'Dispute Fees',
      resizable: true,
      sortable: true,
      field: 'DisputeAmount',
      flex: 1,
      valueFormatter: params => {
        const converted = this.currencyPipe.transform(params.value);
        return converted ? converted : '';
      }
    },
    {
      headerName: 'Net',
      resizable: true,
      sortable: true,
      valueGetter: p => p.data.CharityContribution - p.data.RefundAmount - p.data.DisputeAmount,
      flex: 1,
      valueFormatter: params => {
        const converted = this.currencyPipe.transform(params.value);
        return converted ? converted : '';
      }
    },
    {
      headerName: 'Number of Players',
      resizable: true,
      wrapHeaderText: true,
      sortable: true,
      flex: 1,
      field: 'TotalNumberOfPlayers'
    }
  ];

  // ==========================================================================
  // Game Instance Input
  // ==========================================================================
  @Input() set updateChosenGameInstance(gameInstanceP: IGameInstanceShared) {
    this.chosenGameInstance = gameInstanceP;
    this.generateReport(gameInstanceP);
  }

  public chosenGameInstance?: IGameInstanceShared;

  // ==========================================================================
  // Constructor
  // ==========================================================================
  protected constructor(protected reportingService: ReportingService,
                        protected currencyPipe: FormatCurrencyPipe,
                        protected winnerPayoutStatusPipe: WinnerPayoutStatusPipe,
                        protected pdfUtilityService: PdfUtilityService,
                        protected datePipe: DatePipe) {
    // Initialize help toggle from localStorage
    this.showHelp.set(this.fieldDefinitionsService.getShowHelpPreference());

    // Pre-compute descriptions map once
    this.fieldDescriptions = this.fieldDefinitionsService.getDescriptionsMap();

    // Auto-persist showHelp changes to localStorage
    effect(() => {
      this.fieldDefinitionsService.setShowHelpPreference(this.showHelp());
    });
  }

  // ==========================================================================
  // Abstract Methods
  // ==========================================================================
  abstract generateReport(gameInstanceP: IGameInstanceShared): any;

  // ==========================================================================
  // Shared Methods - Help & Glossary
  // ==========================================================================
  /** Opens the report field glossary dialog */
  openGlossary(): void {
    this.matDialog.open(ReportGlossaryComponent, {
      width: '800px',
      maxHeight: '80vh'
    });
  }

  // ==========================================================================
  // Shared Methods - Grid
  // ==========================================================================
  /** Handles grid ready event and stores grid API reference */
  onGridReady(params: GridReadyEvent<ICharityContributionsBreakdown>): void {
    this.columnApi = params.api;
  }

  /** Determines current sort state for PDF generation */
  protected determineSortForPDF(): IPdfSorting | null {
    const sortedColumn = this.columnApi.getColumnState().find((col) => col.sort !== null);
    if (sortedColumn) {
      return {
        columnToSort: sortedColumn.colId,
        direction: sortedColumn.sort
      }
    }
    return null;
  }

  /** Sorts charity breakdown data based on current grid sort state */
  protected sortColsForPdf(charityBreakdownP: ICharityContributionsBreakdown[], sortP: IPdfSorting): ICharityContributionsBreakdown[] {
    return charityBreakdownP.sort((a, b) => {
      // @ts-ignore
      const propA = a[sortP.columnToSort];
      // @ts-ignore
      const propB = b[sortP.columnToSort];

      if (sortP.direction === "asc") {
        return propA - propB;
      } else if (sortP.direction === "desc") {
        return propB - propA;
      } else {
        return 0;
      }
    });
  }
}
